import { Formik } from 'formik';
import * as Yup from 'yup';
import { generateInsurerAPIKeys, getInsurerProfile, insurerIntegrateWithWebhooks, setMIDCredentials } from '../../../api/requests';
import Page from '../../../components/PageWrapper';
import Button from '../../../components/form-control/Button';
import Input from '../../../components/form-control/Input';
import useUserAccount from '../../../hooks/userAccountHook';
import Loader from '../../../components/Loader';
import { CheckBoxInput } from '../../../components/form-control/CheckBoxInput';
import ToggleSwitch from '../../../components/form-control/ToggleSwitch';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { showToast } from '../../../utils/miscFunctions';

const Developers = () => {
  const { getProfile } = useUserAccount();
  const [loading, setLoading] = useState<boolean>(false);
  const [profileData, setProfileData] = useState<any>();
  const queryClient = useQueryClient();

  useQuery({ queryKey: ['getProfileDetails'], queryFn: () => getProfileDetails() })

  const getProfileDetails = async () => {
    setLoading(true);
    try {
      const response: any = await getInsurerProfile()
      setProfileData(response);
    } catch (error: any) {
      showToast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const integrateWithWebhooks = useMutation({
    mutationFn: (payload: any) => insurerIntegrateWithWebhooks(payload),
    onSuccess: (response: any) => {
      showToast.success(response?.message ?? 'Webhooks integrated successfully');
      queryClient.invalidateQueries({ queryKey: ['getProfileDetails'] });
    },
    onError: (error: any) => {
      showToast.error(error?.description);
    },
    onSettled: () => {
      setLoading(false);
    }
  })

  const generateLiveAPIKey = async () => {
    try {
      const response: any = await generateInsurerAPIKeys('PRODUCTION')
      showToast.success(response?.message ?? 'Live API key generated successfully');
      queryClient.invalidateQueries({ queryKey: ['getProfileDetails'] });
    } catch (error: any) {
      showToast.error(error?.description);
    } finally {
      setLoading(false);
      queryClient.invalidateQueries({ queryKey: ['getProfileDetails'] })
    }
  };

  const generateTestAPIKey = async () => {
    try {
      const response: any = await generateInsurerAPIKeys('SANDBOX')
      showToast.success(response?.message ?? 'Test API key generated successfully');
      queryClient.invalidateQueries({ queryKey: ['getProfileDetails'] });
    } catch (error: any) {
      showToast.error(error?.description);
    } finally {
      setLoading(false);
      queryClient.invalidateQueries({ queryKey: ['getProfileDetails'] })
    }
  };

  const copyTextToClipboard = async (text: string) => {
    if ('clipboard' in navigator) {
      return await navigator.clipboard.writeText(text);
    } else {
      return document.execCommand('copy', true, text);
    }
  }

  const handleCopyClick = async (value: string) => {
    try {
      await copyTextToClipboard(value)
      showToast.success('Copied to clipboard');
    } catch (err) {
      showToast.error('Failed to copy to clipboard');
    }
  };

  return (
    <Page title="Developers">
      <div className="min-h-full bg-white dark:bg-darkBg w-full p-5">
        {loading ? (
         
            <Loader message="Loading. Please wait..." brollyLoaderFixed />
         
        ) : (
          <>
            <div className="">
              <div className="">
                <h1 className="text-black dark:text-darkBg-mainText font-semibold">Developers</h1>
              </div>

              <span className="text-black dark:text-darkBg-subText text-sm">
                Update your developer/integration credentials here.
              </span>
            </div>

            <div className="">
              <Formik
                initialValues={{
                  branchCode: profileData?.branchCode ?? '',
                  midApiKey: profileData?.midApiKey ?? '',
                  midPassword: profileData?.midPassword ?? '',
                  midReady: profileData?.ready ?? false,
                  midUsername: profileData?.midUsername ?? '',
                  nicRegistrationNumber: profileData?.nicRegistration ?? '',
                }}
                validateOnBlur
                validationSchema={Yup.object().shape({
                  branchCode: Yup.string().required('Branch code is required'),
                  nicRegistrationNumber: Yup.string().required('NIC registration number is required'),
                  midUsername: Yup.string().required('MID username is required'),
                  midPassword: Yup.string().required('MID password is required'),
                  midApiKey: Yup.string().required('MID API key is required'),
                })}
                onSubmit={async (values) => {
                  try {
                    const res: any = await setMIDCredentials(values);
                    showToast.success(res?.message ?? 'MID credentials updated successfully');
                  } catch (error: any) {
                    showToast.error(error?.description);
                  } finally {
                    setLoading(false);
                  }
                }}>
                {({ handleBlur, handleChange, handleSubmit, setFieldValue, values, errors, touched }) => (
                  <form onSubmit={handleSubmit}>
                    <>
                      <div className="border border-primary-border dark:border-darkBg-border rounded my-3">
                        <div className="p-4 flex items-center gap-5">
                          <h2 className=" text-black dark:text-darkBg-mainText font-semibold">Interface with MID</h2>
                          <ToggleSwitch
                            label={'Enable interface with MID'}
                            name={'midReady'}
                            checked={values.midReady}
                            onChange={(value) => { setFieldValue('midReady', value) }}
                          />
                        </div>
                        <hr />

                        {values.midReady && (
                          <>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center justify-center p-4">
                              <Input
                                type="text"
                                name="branchCode"
                                label="Branch Code"
                                value={values.branchCode}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={touched.branchCode ? errors.branchCode : undefined}
                                helperText={touched.branchCode ? errors.branchCode : undefined}
                              />
                              <Input
                                type="text"
                                name="nicRegistrationNumber"
                                label="NIC Registration Number"
                                value={values.nicRegistrationNumber}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={touched.nicRegistrationNumber ? errors.nicRegistrationNumber : undefined}
                                helperText={touched.nicRegistrationNumber ? errors.nicRegistrationNumber : undefined}
                              />
                              <Input
                                type="text"
                                name="midUsername"
                                label="MID Username"
                                value={values.midUsername}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={touched.midUsername ? errors.midUsername : undefined}
                                helperText={touched.midUsername ? errors.midUsername : undefined}
                              />
                              <Input
                                type="password"
                                name="midPassword"
                                label="MID Password"
                                showPasswordIcon
                                value={values.midPassword}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={touched.midPassword ? errors.midPassword : undefined}
                                helperText={touched.midPassword ? errors.midPassword : undefined}
                              />
                              <Input
                                type="password"
                                name="midApiKey"
                                label="MID API Key"
                                showPasswordIcon
                                value={values.midApiKey}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={touched.midApiKey ? errors.midApiKey : undefined}
                                helperText={touched.midApiKey ? errors.midApiKey : undefined}
                              />
                            </div>
                            <div className="flex justify-start p-4">
                              <Button
                                label="Save"
                                type="submit"
                                className="px-2 h-[2rem]"
                                bgColor="primary"
                                size="lg"
                                curved
                                disabled={loading}
                                loading={loading}
                              />
                            </div>
                          </>
                        )}
                      </div>

                      <div className="border border-primary-border dark:border-darkBg-border rounded my-3">
                        <div className="p-4">
                          <h2 className=" text-black dark:text-darkBg-mainText font-semibold">API Keys</h2>
                        </div>

                        <hr />

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center p-4">
                          <div className="flex items-center gap-2">
                            <Input
                              value={profileData?.developerSetting?.testKey}
                              label="Test API Key"
                              onClick={() =>
                                handleCopyClick(profileData?.developerSetting?.testKey)
                              }
                              readOnly
                              className="!bg-[#D1D5DB] cursor-pointer"
                            />

                            <Button
                              label="Generate"
                              bgColor="primary"
                              curved
                              onClick={generateTestAPIKey}
                              className="w-fit mt-6 px-5 h-10 cursor-pointer"
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <Input
                              value={profileData?.developerSetting?.liveKey}
                              label="Live API Key"
                              readOnly
                              onClick={() =>
                                handleCopyClick(profileData?.developerSetting?.liveKey)
                              }
                              className="!bg-[#D1D5DB] cursor-pointer"
                            />

                            <Button
                              label="Generate"
                              bgColor="primary"
                              curved
                              onClick={generateLiveAPIKey}
                              className="w-fit mt-6 px-5 h-10 "
                            />
                          </div>
                        </div>
                      </div>
                    </>
                  </form>
                )}
              </Formik>

              <Formik
                initialValues={{
                  enable: profileData?.developerSetting?.enable ?? false,
                  enableForClaims: profileData?.developerSetting?.enableForClaims ?? false,
                  enableForPayouts: profileData?.developerSetting?.enableForPayouts ?? false,
                  enableForPolicies: profileData?.developerSetting?.enableForPolicies ?? false,
                  enableForSales: profileData?.developerSetting?.enableForSales ?? false,
                  webhookUri: profileData?.developerSetting?.webhookUri ?? null
                }}
                validateOnBlur
                validationSchema={Yup.object().shape({
                  webhookUri: Yup.string()
                    .url('Invalid URL')
                    .nullable()
                    .when('enable', {
                      is: true,
                      then: (schema) => schema.required('Webhook URI is required when webhooks are enabled')
                    }),
                })}
                onSubmit={async (values) => {
                  const payload = {
                    enable: values.enable,
                    enableForClaims: values.enableForClaims,
                    enableForPayouts: values.enableForPayouts,
                    enableForPolicies: values.enableForPolicies,
                    enableForSales: values.enableForSales,
                    webhookUri: values.webhookUri
                  }
                  integrateWithWebhooks.mutate(payload)
                }}>
                {({ handleBlur, handleChange, handleSubmit, setFieldValue, values, errors, touched }) => (
                  <form onSubmit={handleSubmit}>
                    <div className="border border-primary-border dark:border-darkBg-border rounded ">
                      <div className="p-4 flex items-center gap-5">
                        <h2 className=" text-black dark:text-darkBg-mainText font-semibold">Enable Webhooks</h2>
                        <>
                          <ToggleSwitch
                            label={'Enable callbacks and webhooks'}
                            name={'enable'}
                            checked={values.enable}
                            onChange={(value: boolean) => {
                              setFieldValue('enable', value);
                            }}
                          />
                        </>
                      </div>
                      <hr />
                      {values.enable && (
                        <>
                          <div className="grid grid-cols-1 gap-6 p-4">
                            <>
                              <CheckBoxInput
                                label={'Enable Webhook for claims'}
                                name={'enableForClaims'}
                                checked={values.enableForClaims}
                                onChange={(value: boolean) => {
                                  setFieldValue('enableForClaims', value);
                                }}
                              />
                              <CheckBoxInput
                                label={'Enable Webhook for payouts'}
                                name={'enableForPayouts'}
                                checked={values.enableForPayouts}
                                onChange={(value: boolean) => {
                                  setFieldValue('enableForPayouts', value);
                                }}
                              />
                              <CheckBoxInput
                                label={'Enable Webhook for policies'}
                                name={'enableForPolicies'}
                                checked={values.enableForPolicies}
                                onChange={(value: boolean) => {
                                  setFieldValue('enableForPolicies', value);
                                }}
                              />
                              <CheckBoxInput
                                label={'Enable Webhook for sales'}
                                name={'enableForSales'}
                                checked={values.enableForSales}
                                onChange={(value: boolean) => {
                                  setFieldValue('enableForSales', value);
                                }}
                              />
                            </>
                            <>
                              <Input
                                type="url"
                                name="webhookUri"
                                label="Webhook URL"
                                value={values.webhookUri}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                placeholder="callback URL"
                                error={touched.webhookUri ? errors.webhookUri : undefined}
                                helperText={touched.webhookUri ? errors.webhookUri : undefined}
                              />
                            </>
                          </div>
                          <div className="flex justify-start p-4">
                            <Button
                              label="Save"
                              type="submit"
                              className="px-2 h-[2rem]"
                              bgColor="primary"
                              size="lg"
                              curved
                              disabled={loading}
                              loading={loading}
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </form>
                )}
              </Formik>
            </div>
          </>
        )}
      </div>
    </Page>
  );
};

export default Developers;
